import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight, Loader2, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('register'); // 'register' | 'verify'
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) { toast.error('Bitte stimmen Sie den AGB und der Datenschutzerklärung zu.'); return; }
    if (form.password.length < 6) { toast.error('Passwort muss mindestens 6 Zeichen lang sein'); return; }
    setLoading(true);
    try {
      const res = await register(form.email, form.password, form.name, form.company_name);
      if (res?.email_verification_required === false) {
        toast.success('Konto erstellt! Weiter zur Einrichtung.');
        navigate('/onboarding');
      } else {
        toast.success('Konto erstellt! Bitte bestätigen Sie Ihre E-Mail-Adresse.');
        setStep('verify');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifyCode.length !== 6) { toast.error('Bitte geben Sie den 6-stelligen Code ein'); return; }
    setVerifyLoading(true);
    try {
      await api.post('/auth/verify-email', { code: verifyCode });
      toast.success('E-Mail bestätigt! Weiter zur Einrichtung.');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Neuer Code gesendet!');
    } catch {
      toast.error('Code konnte nicht gesendet werden');
    } finally {
      setResendLoading(false);
    }
  };

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-700 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-transparent to-emerald-900/30" />
        <div className="relative z-10 max-w-md">
          <div className="bg-white rounded-xl p-3 w-fit mb-8">
            <img src="/logo.png" alt="LaizyLeads" className="h-10 w-auto" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 tracking-tight">Neue B2B-Kunden entdecken</h1>
          <p className="text-lg text-emerald-100 leading-relaxed">Registrieren Sie Ihr Unternehmen und lassen Sie KI Ihre idealen B2B-Kunden finden. Keine manuelle Recherche mehr.</p>
          <div className="mt-8 space-y-3">
            {['Automatische Website-Analyse', 'KI-gestützte ICP-Erstellung', 'Priorisierte Lead-Erkennung'].map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-emerald-100">
                <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-emerald-300" />
                </div>
                <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        {step === 'register' ? (
          <Card className="w-full max-w-md border-0 shadow-none">
            <CardHeader className="space-y-1 pb-6">
              <div className="lg:hidden flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="LaizyLeads" className="h-8 w-auto" />
                <span className="font-semibold text-lg">LaizyLeads</span>
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight">Konto erstellen</CardTitle>
              <CardDescription>Registrieren Sie Ihr Unternehmen in wenigen Schritten</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Firmenname</Label>
                  <Input id="company" placeholder="Meine Firma GmbH" value={form.company_name} onChange={update('company_name')} required data-testid="register-company-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Ihr Name</Label>
                  <Input id="name" placeholder="Max Mustermann" value={form.name} onChange={update('name')} required data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Geschäftliche E-Mail</Label>
                  <Input id="email" type="email" placeholder="name@firma.de" value={form.email} onChange={update('email')} required data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <Input id="password" type="password" placeholder="Min. 6 Zeichen" value={form.password} onChange={update('password')} required data-testid="register-password-input" />
                </div>
                <div className="flex items-start gap-2.5 pt-1">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                    data-testid="register-terms-checkbox"
                    className="mt-0.5"
                  />
                  <label htmlFor="terms" className="text-xs text-gray-500 leading-relaxed cursor-pointer select-none">
                    Ich stimme den{' '}
                    <a href="https://laizyones.de/agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">AGB</a>
                    {' '}und der{' '}
                    <a href="https://laizyones.de/datenschutz" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline font-medium">Datenschutzerklärung</a>
                    {' '}zu.
                  </label>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading || !acceptedTerms} data-testid="register-submit-btn">
                  {loading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Konto wird erstellt...</span>
                  ) : (
                    <span className="flex items-center gap-2">Konto erstellen <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </form>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Bereits ein Konto?{' '}
                <Link to="/login" className="text-emerald-600 font-medium hover:underline" data-testid="login-link">Anmelden</Link>
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="w-full max-w-md border-0 shadow-none" data-testid="verify-email-card">
            <CardHeader className="space-y-1 pb-6 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-bold tracking-tight">E-Mail bestätigen</CardTitle>
              <CardDescription>
                Wir haben einen 6-stelligen Code an <strong className="text-foreground">{form.email}</strong> gesendet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Verifizierungscode</Label>
                  <Input
                    id="code"
                    placeholder="123456"
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-[0.3em] font-mono"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="verify-code-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={verifyLoading || verifyCode.length !== 6} data-testid="verify-submit-btn">
                  {verifyLoading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Wird geprüft...</span>
                  ) : (
                    <span className="flex items-center gap-2">Bestätigen <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <Button variant="ghost" size="sm" onClick={handleResend} disabled={resendLoading} className="text-muted-foreground" data-testid="resend-code-btn">
                  {resendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Code erneut senden
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
